import { DOWNTIMEMETHODCAPTURE } from "constants/constants";
import { ClusterCachedDataContext } from "contexts/ClusterCachedDataContext/ClusterCachedDataContext";
import { HMICachedDataContext } from "contexts/HMICachedDataContext/HMICachedDataContext";
import { MQTTBrokerContext } from "contexts/MQTTBrokerContext/MQTTBrokerContext";
import { EventListeningEntity } from "mqtt/entities/EventReport/EventListeningEntity";
import { IPLCEvent } from "mqtt/entities/EventReport/EventReportInterfaces";
import { MqttClient } from "precompiled-mqtt";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { getClusterIdHMI } from "utilities/storageUtilities";

function useMQTTListener(onMessage: (message: IPLCEvent) => void, isPlc: boolean) {
    const client = useRef<MqttClient>();
    const [isListening, setIsListening] = useState(false);
    const [error, setError] = useState<Error>();
    const { currentSetting } = useContext(MQTTBrokerContext);
    const {
        store: { machineCenter: machineCenterHMI },
    } = useContext(HMICachedDataContext);
    const {
        store: { machineCentersCluster, clusterDetails },
    } = useContext(ClusterCachedDataContext);
    const isCluster = getClusterIdHMI();
    const HMIClusterCode = useMemo(
        () => (isCluster ? clusterDetails?.cluster_code || machineCenterHMI?.hmi_code : machineCenterHMI?.hmi_code),
        [clusterDetails, machineCenterHMI, isCluster],
    );
    const HMIClusterPlcAddress = useMemo(
        () =>
            isCluster
                ? machineCentersCluster
                      ?.filter(
                          (machineCenterCluster) =>
                              machineCenterCluster.downtime_method_capture_id === DOWNTIMEMETHODCAPTURE.PLC &&
                              Boolean(machineCenterCluster.plc_address),
                      )
                      ?.map((machineCenterCluster) => machineCenterCluster.plc_address) ||
                  (machineCenterHMI?.plc_address ? [machineCenterHMI?.plc_address] : undefined)
                : machineCenterHMI?.plc_address
                ? [machineCenterHMI?.plc_address]
                : undefined,
        [machineCentersCluster, machineCenterHMI, isCluster],
    );

    useEffect(() => {
        if (isPlc && currentSetting && HMIClusterCode && HMIClusterPlcAddress) {
            const startListening = async () => {
                try {
                    const listeningClient = await EventListeningEntity.startListening(
                        onMessage,
                        setError,
                        HMIClusterPlcAddress as string[],
                        currentSetting,
                        HMIClusterCode,
                    );
                    setIsListening(true);
                    client.current = listeningClient;
                } catch (error) {
                    setError(error as Error);
                }
            };
            startListening();
            return () => {
                if (client.current) {
                    client.current.end();
                }
            };
        }
    }, [currentSetting, isPlc, onMessage, HMIClusterCode, HMIClusterPlcAddress]);

    return { isListening, error };
}

export default useMQTTListener;
